import type { ProxyResolution } from '../types/nutrition';

type SnpMap = { [rsid: string]: { genotype: string; chromosome: string; position: number } };

/**
 * Resolve a variant against parsed SNPs, falling back to ordered proxy rsIDs
 * when the primary isn't present. Returns null if neither hits.
 */
export const resolveProxies = ({
  snps,
  primaryRsid,
  proxies = [],
}: {
  snps: SnpMap;
  primaryRsid: string;
  proxies?: string[];
}): ProxyResolution | null => {
  if (snps[primaryRsid]) {
    return {
      primaryRsid,
      foundRsid: primaryRsid,
      isPrimary: true,
      genotype: snps[primaryRsid].genotype,
    };
  }

  for (const proxy of proxies) {
    if (snps[proxy]) {
      return {
        primaryRsid,
        foundRsid: proxy,
        isPrimary: false,
        genotype: snps[proxy].genotype,
      };
    }
  }

  return null;
};

/** Flatten primary + proxy rsIDs across all tab configs into one filter set. */
export const buildTargetRsidSet = (
  tabConfigs: Array<{
    rules: { [gene: string]: { [rsid: string]: { proxies?: string[] } } };
  }>,
): Set<string> => {
  const targetRsids = new Set<string>();
  for (const tabConfig of tabConfigs) {
    for (const geneRules of Object.values(tabConfig.rules)) {
      for (const [primaryRsid, rule] of Object.entries(geneRules)) {
        targetRsids.add(primaryRsid);
        rule.proxies?.forEach((proxy) => targetRsids.add(proxy));
      }
    }
  }
  return targetRsids;
};

export const resolveGeneVariants = ({
  geneRules,
  snps,
}: {
  geneRules: { [rsid: string]: { proxies?: string[] } };
  snps: SnpMap;
  geneName: string;
}): Array<{ primaryRsid: string; resolution: ProxyResolution | null }> =>
  Object.entries(geneRules).map(([primaryRsid, rule]) => ({
    primaryRsid,
    resolution: resolveProxies({ snps, primaryRsid, proxies: rule.proxies || [] }),
  }));

export const generateProxyStats = (
  resolutions: Array<{ primaryRsid: string; resolution: ProxyResolution | null }>,
): {
  totalVariants: number;
  foundVariants: number;
  primaryFound: number;
  proxyFound: number;
  proxyUsageRate: number;
} => {
  const totalVariants = resolutions.length;
  const found = resolutions.filter((r) => r.resolution !== null);
  const primaryFound = found.filter((r) => r.resolution!.isPrimary).length;
  const proxyFound = found.length - primaryFound;
  const proxyUsageRate = found.length > 0 ? (proxyFound / found.length) * 100 : 0;

  return {
    totalVariants,
    foundVariants: found.length,
    primaryFound,
    proxyFound,
    proxyUsageRate: Math.round(proxyUsageRate * 10) / 10,
  };
};
